import React from 'react';
import Accordion from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/accordion';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import * as screensizes from '@loomstateorg/shared-frontend/utils/screensizes';
import { Reference } from 'types/reference';
import { NextFrontasticImage } from 'frontastic/lib/image';
import Column, { FooterLink } from './components/column';
import FooterBottom from './footer-bottom';

export interface StyleProps {
  backgroundImage?: string;
  backgroundColor?: string;
  fontColor?: string;
}
export interface FooterColumn {
  header?: string;
  links?: FooterLink[];
}
export interface Props {
  columns: FooterColumn[];
  logo?: NextFrontasticImage;
  socialMedia?: SocialMedia[];
  styleProps?: StyleProps;
  columnHeaderClassName?: string;
}

export interface SocialMedia {
  logo: NextFrontasticImage;
  reference: Reference;
}

const Footer: React.FC<Props> = ({ columns, logo, socialMedia, styleProps, columnHeaderClassName }) => {
  const [isBiggerThanTabletSize] = useMediaQuery(screensizes.desktop);

  const gridClasses = [
    '', // 0 columns - not used
    'grid-cols-1',
    'grid-cols-2',
    'grid-cols-3',
    'grid-cols-4',
    'grid-cols-5',
    'grid-cols-6',
  ];

  const gridClass = gridClasses[Math.min(columns.length, 6)]; // This will ensure that max columns is 6, more than 6 will break to another row

  return (
    <footer
      aria-labelledby="footer-heading"
      data-testid="footer"
      className={`text-[ w-full${styleProps?.fontColor}]`}
      style={
        styleProps?.backgroundImage
          ? {
              backgroundImage: `url(${styleProps?.backgroundImage})`,
              backgroundAttachment: 'fixed',
              backgroundSize: '100% 100%',
              color: styleProps?.fontColor,
            }
          : {
              zIndex: -1,
              backgroundColor: `${styleProps?.backgroundColor}`,
              color: styleProps?.fontColor,
            }
      }
    >
      {isBiggerThanTabletSize ? (
        <div className="flex w-full justify-between px-24 py-58 lg:px-20 xl:px-48">
          <div className={`grid w-full ${gridClass}`}>
            {columns?.map((column, index) => (
              <div key={index} className="mx-10" data-testid={`footer-column-${index}`}>
                <Column
                  header={column.header}
                  headerClassName={columnHeaderClassName}
                  links={column.links ?? []}
                  className={`text-[ flex flex-col items-start${styleProps?.fontColor}]`}
                  index={index}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <ul className="flex flex-col" data-testid="footer-column-list">
          {columns?.map((column, index) => (
            <li key={index} className="border-b border-neutral-300" data-testid={`footer-column-${index}`}>
              <Accordion
                variant="arrow"
                closedSectionTitle={column.header ?? ''}
                openSectionTitle={column.header}
                buttonClassName="h-48 text-14 font-medium text-neutral-100 px-12 text-neutral-150"
                iconClassName="text-neutral-100"
              >
                <div className="mb-16 ml-20 flex flex-row text-14 md:ml-24" key={index}>
                  <Column links={column.links ?? []} className="px-0" index={index} />
                </div>
              </Accordion>
            </li>
          ))}
        </ul>
      )}
      <div className="w-full sm:border-t sm:border-secondary-light" />
      <FooterBottom socialMedia={socialMedia} logo={logo} />
    </footer>
  );
};

export default Footer;
